'use client';

import React, { useEffect } from 'react';
import useAgentSummary from '../../hooks/useAgentSummary';
import { FinanceKpiStrip } from '../../components/crm/billing/SalesFinance';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  BarChart3, Users, TrendingUp, Activity,
  Wallet, MessageCircle,
  Phone, Mail, Calendar, ArrowUpRight, Crosshair,
  Plus, Download, Search, MoreVertical, ChevronRight,
  TrendingDown, Zap
} from 'lucide-react';
import { fetchCrmDashboard, fetchFunnel, fetchRevenueForecast, fetchActivities, fetchForecastByRep } from '../../redux/crmSlice';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { PageLoader } from '../../components/crm/ui';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const ACTIVITY_ICONS = {
  message: MessageCircle,
  whatsapp_message: MessageCircle,
  call: Phone,
  email: Mail,
  meeting: Calendar,
  note: Activity,
  lead_created: Users,
  deal_created: TrendingUp,
  stage_change: TrendingUp,
  default: Activity,
};

export default function CrmDashboardPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { analytics, loading, error } = useSelector(state => state.crm);

  useEffect(() => {
    dispatch(fetchCrmDashboard());
    dispatch(fetchFunnel());
    dispatch(fetchRevenueForecast());
    dispatch(fetchForecastByRep());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      alertService.error('Error', typeof error === 'string' ? error : 'Failed to load CRM data.');
    }
  }, [error]);

  const dash = analytics.dashboard || {};
  const funnel = analytics.funnel || [];
  const forecast = analytics.forecast || [];
  const forecastByRep = analytics.forecastByRep || [];
  const recentActs = dash.recentActivities || [];

  if (loading && !dash.totalLeads) {
    return (
      <PageLoader />
    );
  }

  const handleExportReport = () => {
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      'Section,Metric,Value',
      `Summary,${escape('Total Leads')},${escape(dash.totalLeads || 0)}`,
      `Summary,${escape('New Leads This Month')},${escape(dash.newLeads || 0)}`,
      `Summary,${escape('Active Deals')},${escape(dash.openDeals || 0)}`,
      `Summary,${escape('Won Deals')},${escape(dash.wonDeals || 0)}`,
      `Summary,${escape('Open Pipeline Value')},${escape(dash.openDealValue || 0)}`,
      ...funnel.map(stage => `Funnel,${escape(stage._id || 'Unknown')},${escape(stage.count || 0)}`),
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crm-dashboard-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    alertService.success('Exported', 'Dashboard report downloaded');
  };

  return (
    <div className="py-4 sm:py-6 px-0 sm:px-4 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <PageHeader
        title="Revenue Dashboard"
        icon={TrendingUp}
        subtitle="Pipeline, revenue and activity at a glance."
        actions={<>
<button onClick={handleExportReport} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] transition-all flex items-center gap-2 whitespace-nowrap shrink-0">
            <Download size={14} /> Export Report
          </button>
          <button
            onClick={() => navigate('/dashboard/crm/leads', { state: { openCreate: true } })}
            className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2 whitespace-nowrap shrink-0"
          >
            <Plus size={14} /> Fresh Lead
          </button>
  </>}
      />

      <FinanceKpiStrip />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard 
          label="Total Leads" 
          value={dash.totalLeads || 0} 
          subValue={`+${dash.newLeads || 0} this month`}
          icon={Users}
          color="blue"
        />
        <KPICard 
          label="Active Deals" 
          value={dash.openDeals || 0} 
          subValue={`${dash.wonDeals || 0} won recently`}
          icon={TrendingUp}
          color="emerald"
        />
        <KPICard 
          label="Pipeline Value" 
          value={formatCurrency(dash.openDealValue || 0, dash.baseCurrency || 'INR', { compact: true })}
          subValue="Projected Revenue"
          icon={Wallet}
          color="violet"
        />
        <AgentKpi />
      </div>

      <DailyBriefCard />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Conversion Funnel */}
        <div className="lg:col-span-2 sf-card">
          <div className="sf-card-header">
            <h2 className="sf-card-title flex items-center gap-2">
              <Crosshair size={16} className="text-[#0176D3]" /> Sales Conversion Funnel
            </h2>
            <MoreVertical size={16} className="text-[#706E6B] cursor-pointer" />
          </div>
          <div className="sf-card-body p-8">
            <div className="space-y-6">
              {funnel.length > 0 ? funnel.map((stage, idx) => {
                const max = funnel[0].count;
                const pct = Math.round((stage.count / max) * 100);
                return (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between text-[12px] font-bold uppercase tracking-wider text-[#706E6B]">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#0176D3]" />
                        {stage._id || 'Unknown'}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[#080707]">{stage.count} Leads</span>
                        <span className="bg-[#F3F2F2] px-2 py-0.5 rounded text-[10px]">{pct}%</span>
                      </div>
                    </div>
                    <div className="h-3 w-full bg-[#F3F2F2] rounded-full overflow-hidden border border-[#D8DDE6]">
                      <div 
                        className="h-full bg-[#0176D3] transition-all duration-1000 ease-out" 
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              }) : (
                <div className="py-20 text-center text-[#706E6B]">
                  <BarChart3 className="mx-auto mb-4 opacity-20" size={48} />
                  <p className="text-sm font-medium">Insufficient data for funnel visualization</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity Timeline */}
        <div className="sf-card flex flex-col h-full">
          <div className="sf-card-header">
            <h2 className="sf-card-title flex items-center gap-2">
              <Activity size={16} className="text-[#2E844A]" /> Business Activity
            </h2>
            <span className="sf-badge sf-badge-success">Live</span>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-[400px] max-h-[600px]">
            {recentActs.length > 0 ? recentActs.map((act, idx) => {
              const Icon = ACTIVITY_ICONS[act.type] || ACTIVITY_ICONS.default;
              return (
                <div key={idx} className="flex gap-4 relative group">
                  {idx !== recentActs.length - 1 && (
                    <div className="absolute left-4 top-10 bottom-0 w-[1px] bg-[#D8DDE6] group-hover:bg-[#0176D3] transition-colors" />
                  )}
                  <div className="w-8 h-8 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.08)] z-10 group-hover:border-[#0176D3] transition-all">
                    <Icon size={14} className="text-[#706E6B] group-hover:text-[#0176D3]" />
                  </div>
                  <div className="flex-1 pb-6">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-[13px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors">{act.title}</h4>
                      <time className="text-[10px] text-[#706E6B] font-medium">{timeAgo(act.createdAt)}</time>
                    </div>
                    <p className="text-[12px] text-[#706E6B] line-clamp-2 leading-relaxed">{act.description || 'Activity recorded'}</p>
                  </div>
                </div>
              );
            }) : (
              <div className="flex flex-col items-center justify-center h-full opacity-20 py-20">
                <Activity size={48} />
                <p className="text-xs font-bold uppercase mt-4">Queue Empty</p>
              </div>
            )}
          </div>
          <div className="p-4 border-t border-[#D8DDE6] bg-[#F4F6F9]">
            <button onClick={() => navigate('/dashboard/crm/activities')} className="w-full h-10 bg-white border border-[#D8DDE6] rounded text-[12px] font-bold text-[#0176D3] hover:bg-[#F3F2F2] transition-all">
              Full Activity History
            </button>
          </div>
        </div>
      </div>

      {/* Forecast Section */}
      <div className="sf-card overflow-hidden">
        <div className="sf-card-header bg-[#F4F6F9]">
          <h2 className="sf-card-title flex items-center gap-2">
            <TrendingUp size={16} className="text-[#0176D3]" /> Revenue Forecast by Stage
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="sf-table">
            <thead>
              <tr>
                <th>Stage Name</th>
                <th>Confidence</th>
                <th>Opportunities</th>
                <th className="text-right">Expected Revenue</th>
              </tr>
            </thead>
            <tbody>
              {forecast.length > 0 ? forecast.map((f, idx) => (
                <tr key={idx}>
                  <td className="font-bold text-[#0176D3]">{f._id || 'N/A'}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-[#F3F2F2] rounded-full overflow-hidden border border-[#D8DDE6]">
                        <div className="h-full bg-[#0176D3]" style={{ width: `${Math.min(100, Math.max(10, f.count * 10))}%` }} />
                      </div>
                      <span className="text-[11px] font-bold">{Math.min(100, f.count * 10)}%</span>
                    </div>
                  </td>
                  <td className="font-medium">{f.count} Accounts</td>
                  <td className="text-right font-bold text-[#080707]">{formatCurrency(f.totalValue, dash.baseCurrency || 'INR', { compact: true })}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-[#706E6B] text-sm">
                    No active deals in pipeline for forecasting.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Forecast Rollup by Rep */}
      <div className="sf-card overflow-hidden">
        <div className="sf-card-header bg-[#F4F6F9]">
          <h2 className="sf-card-title flex items-center gap-2">
            <Users size={16} className="text-[#0176D3]" /> Revenue Forecast by Rep
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="sf-table">
            <thead>
              <tr>
                <th>Rep</th>
                <th>Deal Count</th>
                <th className="text-right">Weighted Forecast</th>
              </tr>
            </thead>
            <tbody>
              {forecastByRep.length > 0 ? forecastByRep.map((r, idx) => (
                <tr key={idx}>
                  <td className="font-bold text-[#0176D3]">{r.repName || r.assignedTo || 'Unassigned'}</td>
                  <td className="font-medium">{r.dealCount} Deals</td>
                  <td className="text-right font-bold text-[#080707]">
                    {(r.breakdown || []).map((b, bIdx) => (
                      <span key={bIdx} className="inline-block ml-2">
                        {formatCurrency(b.weightedForecast, b.currency || dash.baseCurrency || 'INR', { compact: true })}
                      </span>
                    ))}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={3} className="py-12 text-center text-[#706E6B] text-sm">
                    No open deals assigned to a rep yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Real numbers from the agents: what is waiting for a person, how much they did today, how often people say yes.
function AgentKpi() {
  const { summary } = useAgentSummary();
  const navigate = useNavigate();
  if (!summary) {
    return (
      <button type="button" onClick={() => navigate('/dashboard/agents')} className="text-left">
        <KPICard label="AI Agents" value="Off" subValue="Turn on agents in the Agent Hub" icon={Zap} color="amber" />
      </button>
    );
  }
  const accepted = summary.acceptanceRate == null ? 'no decisions yet' : `${summary.acceptanceRate}% accepted`;
  return (
    <button type="button" onClick={() => navigate('/dashboard/agents')} className="text-left">
      <KPICard label="Agent proposals" value={summary.pendingCount} subValue={`${summary.runsToday} runs today · ${accepted}`} icon={Zap} color="amber" />
    </button>
  );
}

// The morning brief the daily brief agent wrote for the signed-in person.
function DailyBriefCard() {
  const [brief, setBrief] = React.useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    let live = true;
    api.get('/agents/brief').then(({ data }) => { if (live) setBrief(data.data); }).catch(() => {});
    return () => { live = false; };
  }, []);
  if (!brief) return null;
  const route = { lead: 'crm/leads', deal: 'crm/deals', invoice: 'crm/invoices' };
  return (
    <div className="rounded-lg border border-[#E5E5E5] bg-white p-4 mb-4">
      <p className="text-sm font-semibold text-[#181818]">Your day</p>
      <p className="text-sm text-[#444] mb-2">{brief.headline}</p>
      <ul className="space-y-1">
        {brief.items.map((i, n) => (
          <li key={n} className="text-sm text-[#181818]">
            {i.entity && route[i.kind] ? (
              <button type="button" className="text-left text-[#0176D3] hover:underline" onClick={() => navigate(`/dashboard/${route[i.kind]}/${i.entity.id}`)}>{i.text}</button>
            ) : i.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

function KPICard({ label, value, subValue, icon: Icon, color, isMock }) {
  const colorMap = {
    blue: 'bg-[#0176D3]/10 text-[#0176D3] border-[#0176D3]/20',
    emerald: 'bg-[#2E844A]/10 text-[#2E844A] border-[#2E844A]/20',
    violet: 'bg-[#0176D3]/10 text-[#0176D3] border-[#0176D3]/20',
    amber: 'bg-[#FF9900]/10 text-[#FF9900] border-[#FF9900]/20',
  };

  return (
    <div className="sf-card p-6 flex items-start gap-4">
      <div className={cn("w-12 h-12 rounded-[4px] flex items-center justify-center shrink-0 border", colorMap[color])}>
        <Icon size={24} strokeWidth={1.5} />
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider leading-none">{label}</p>
          {isMock && <span className="text-[8px] bg-[#F3F2F2] px-1 rounded font-bold uppercase text-[#706E6B]">AI</span>}
        </div>
        <h3 className="text-2xl font-bold text-[#080707] tabular-nums leading-none mb-1">{value}</h3>
        <p className="text-[11px] font-medium text-[#706E6B]">{subValue}</p>
      </div>
    </div>
  );
}
